import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { SemboDate } from '@libs/sembo-dates';
import { getDomain } from '@libs/utils';
import type {
  ActivityTicket,
  AgeRange,
  DayTicketRate,
  Money,
  TicketRate,
} from '@libs/trip-planner-interfaces';

import type { ActivityDetailsData } from '../models';

const { t } = i18n.global;

export function getIngredientStayDuration(
  startDate: Date,
  numberOfNights: number
): Date[] {
  const endDate = new SemboDate(startDate).add({ days: numberOfNights }).get();

  return SemboDate.rangeOfDates(startDate, endDate);
}

export function getSelectedRatesByOffset(
  offset: number,
  activityAvailability: DayTicketRate[],
  currentDate?: Date
): TicketRate[] {
  if (!currentDate) {
    return [];
  }

  const selectedDate = new SemboDate(currentDate).add({ days: offset }).get();

  return (
    activityAvailability.find((dayTicketRate: DayTicketRate) =>
      new SemboDate(dayTicketRate.date).isSameDay(selectedDate)
    )?.rates ?? []
  );
}

export function getActivityTotalPrice(
  selectedActivityTickets: ActivityTicket[],
  activityAvailability: DayTicketRate[],
  preferredDay?: Date,
  currentDate?: Date
): Money {
  const localeMoneyFormat = getDomain.getCurrentDomainSpec().MoneyFormat;

  let currency = localeMoneyFormat.currency;
  let currencySymbol = localeMoneyFormat.currencySymbol;

  if (!selectedActivityTickets || !selectedActivityTickets.length) {
    return {
      amount: 0,
      currency,
      currencySymbol,
    };
  }

  let activityTotalAmount = 0;

  if (activityAvailability?.length) {
    activityTotalAmount = selectedActivityTickets.reduce(
      (totalActivityAmount, selectedTicket, index) => {
        const selectedTicketPrice = getSelectedTicketPrice(
          activityAvailability,
          selectedTicket,
          preferredDay,
          currentDate
        );

        if (!selectedTicketPrice) {
          return 0;
        }

        if (index === 0) {
          currency = selectedTicketPrice.currency;
          currencySymbol = selectedTicketPrice.currencySymbol;
        }

        totalActivityAmount +=
          selectedTicket.quantity * selectedTicketPrice.amount;

        return totalActivityAmount;
      },
      0
    );
  }

  return {
    amount: activityTotalAmount,
    currency,
    currencySymbol,
  };
}

function getSelectedTicketPrice(
  activityAvailability: DayTicketRate[],
  selectedTicket: ActivityTicket,
  preferredDay?: Date,
  currentDate?: Date
): Money | null {
  const selectedTicketAvailability = getSelectedTicketAvailabilty(
    activityAvailability,
    selectedTicket,
    preferredDay,
    currentDate
  );

  if (!selectedTicketAvailability) {
    return null;
  }

  return getPriceForAgeRange(
    selectedTicketAvailability,
    selectedTicket.timeSlotId
  );
}

export function getSelectedTicketAvailabilty(
  activityAvailability: DayTicketRate[],
  selectedTicket: ActivityTicket,
  preferredDay?: Date,
  currentDate?: Date
): AgeRange | undefined {
  const dayAvailabilityRates = getDayAvailabilityRates(
    activityAvailability,
    selectedTicket,
    preferredDay,
    currentDate
  );

  return dayAvailabilityRates
    .find((rate) => rate.rateId === selectedTicket.rateId)
    ?.ageRanges.find(
      (ageRange) => ageRange.ageRangeId === selectedTicket.ageRangeId
    );
}

export function getPriceForAgeRange(
  ageRange: AgeRange,
  timeSlotId?: string
): Money {
  const timeSlotPrice = ageRange.availableTimeslotsPrices?.find(
    (timeSlotPrice) => timeSlotPrice.availabletimeSlotId === timeSlotId
  );

  return timeSlotPrice ?? ageRange.price;
}
function getDayAvailabilityRates(
  activityAvailability: DayTicketRate[],
  selectedTicket: ActivityTicket,
  preferredDay?: Date,
  currentDate?: Date
): TicketRate[] {
  if (preferredDay) {
    return getSelectedRatesByOffset(
      selectedTicket.dateOffset,
      activityAvailability,
      currentDate
    );
  } else {
    const displayingRates: TicketRate[] =
      getRatesToDisplay(activityAvailability);
    return displayingRates.filter(
      (rate) => rate.rateId === selectedTicket.rateId
    );
  }
}

export function getRatesToDisplay(
  activityAvailability: DayTicketRate[]
): TicketRate[] {
  const ratesAvailability = activityAvailability.flatMap(
    (dayTicketRates) => dayTicketRates.rates
  );

  const groupedRates = ratesAvailability.reduce((acc, ticketRate) => {
    const rateId = ticketRate.rateId;
    if (!acc.has(rateId)) {
      acc.set(rateId, ticketRate);
    }
    return acc;
  }, new Map<string, TicketRate>());

  return Array.from(groupedRates.values());
}

export function getAgeRangeAmountText(
  ageRange: AgeRange,
  selectedTimeSlotId?: string
): string | undefined {
  const MAXIMUM_AMOUNT_THRESHOLD = 20;
  const amountTexts: string[] = [];

  if (ageRange.minimumTicketsPerAgeRange) {
    amountTexts.push(
      `${t('planner.min')}: ${ageRange.minimumTicketsPerAgeRange}`
    );
  }

  const selectedTimeSlot = ageRange.availableTimeslotsPrices?.find(
    (timeSlot) => timeSlot.availabletimeSlotId === selectedTimeSlotId
  );

  const quantityAvailable =
    selectedTimeSlot?.quantityAvailable ?? ageRange.quantityAvailable;

  if (quantityAvailable && quantityAvailable < MAXIMUM_AMOUNT_THRESHOLD) {
    amountTexts.push(`${t('planner.max')}: ${quantityAvailable}`);
  }

  if (!amountTexts.length) {
    return;
  }

  return ` | ${amountTexts.join(', ')}`;
}

export function hasActivityAvailabilityInSuggestedDates(
  activityDetails: ActivityDetailsData
): boolean {
  // NOTE: isBetween() does not include values, it does start < x < end. Since we need to include these values to get start <= x <= end, we need to substract 1 day to startDate and add 1 day to endDate
  const startDate = new SemboDate(activityDetails.ingredientDate)
    .sub({ days: 1 })
    .get();
  const endDate = new SemboDate(activityDetails.ingredientDate)
    .add({
      days: activityDetails.numberOfNights + 1,
    })
    .get();
  const activityHasAvailabilityInSuggestedDates =
    activityDetails.availability.some(({ date }) => {
      const availabilityDate = new SemboDate(date);
      return availabilityDate.isBetween(startDate, endDate);
    });

  return activityHasAvailabilityInSuggestedDates;
}
